import { useState, type ReactNode } from 'react';
import {
  DisclosureChevron,
  EmptyState,
  Pill,
  ProgressBar,
  SectionHead,
  TypeChip
} from '../primitives';
import { computeProgress, directChildren } from './helpers';
import type { ProjectWorkItem } from './types';

export interface EpicsSectionProps {
  items: ReadonlyArray<ProjectWorkItem>;
  onOpenWorkItem: (id: string) => void;
  /** "New ▾" header control (create-or-attach menu). */
  newMenu?: ReactNode;
}

export function EpicsSection({ items, onOpenWorkItem, newMenu }: EpicsSectionProps) {
  const epics = items.filter((i) => i.type === 'epic');

  return (
    <section className="pn-section" data-testid="epics-section">
      <SectionHead title="Epics" tag={String(epics.length)} action={newMenu} />
      {epics.length === 0 ? (
        <EmptyState data-testid="epics-empty">No epics in this project yet.</EmptyState>
      ) : (
        <div className="pn-epics">
          {epics.map((epic) => (
            <EpicRow key={epic.id} epic={epic} items={items} onOpenWorkItem={onOpenWorkItem} />
          ))}
        </div>
      )}
    </section>
  );
}

function EpicRow({
  epic,
  items,
  onOpenWorkItem
}: {
  epic: ProjectWorkItem;
  items: ReadonlyArray<ProjectWorkItem>;
  onOpenWorkItem: (id: string) => void;
}) {
  const [open, setOpen] = useState(true);
  const children = directChildren(items, epic.id) as ProjectWorkItem[];
  const { done, total } = computeProgress(children);
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;

  return (
    <article
      className="pn-epic"
      data-open={open ? '1' : '0'}
      data-testid={`epic-row-${epic.id}`}
    >
      <div className="pn-epic-row">
        <DisclosureChevron
          expanded={open}
          aria-label={open ? `Collapse ${epic.name}` : `Expand ${epic.name}`}
          data-testid={`epic-toggle-${epic.id}`}
          onClick={() => setOpen((v) => !v)}
        />
        <div className="pn-epic-main">
          <div className="pn-epic-titleline">
            <button
              type="button"
              className="pn-epic-name pn-card-btn"
              onClick={() => onOpenWorkItem(epic.id)}
            >
              {epic.name}
            </button>
            <Pill status={epic.pill}>{epic.status}</Pill>
          </div>
          <div className="pn-epic-sub">
            {children.length} work items · {done} done
          </div>
        </div>
        <div className="pn-epic-right">
          <ProgressBar
            value={done}
            total={total}
            label={`${epic.name} progress`}
            size="sm"
          />
          <span className="pn-epic-pct">{pct}%</span>
        </div>
      </div>
      {open ? (
        <div className="pn-epic-body">
          {children.length === 0 ? (
            <div className="pn-epic-empty">No child work items.</div>
          ) : (
            children.map((c) => (
              <button
                key={c.id}
                type="button"
                className={`pn-mini-item pn-card-btn${c.isCompleted ? ' is-done' : ''}`}
                onClick={() => onOpenWorkItem(c.id)}
              >
                <span className="pn-mini-check" data-done={c.isCompleted ? '1' : '0'} aria-hidden="true" />
                <span className="pn-mini-title">{c.name}</span>
                <TypeChip>{c.type}</TypeChip>
                {!c.isCompleted && c.pill !== 'todo' ? (
                  <Pill status={c.pill}>{c.status}</Pill>
                ) : null}
              </button>
            ))
          )}
        </div>
      ) : null}
    </article>
  );
}
